import loadable from '@loadable/component';
import { OverlaySpinner } from '@ankr.com/ui';
import { Navigate } from 'react-router';

import { AccountPermission } from 'modules/permissions/constants';
import { GuardAccountPermission } from 'modules/account/components/GuardAccountPermission';
import { LoggingInGuard } from 'modules/auth/components/LoggingInGuard';
import { NoSSRGuard } from 'modules/common/components/NoSSRGuard';
import { Page } from 'modules/layout/components/Page';
import { ProjectsAccessGuard } from 'modules/projects/components/ProjectsAccessGuard';
import { chainsRoutesConfig } from 'domains/chains/routes';
import { useAccount } from 'modules/account/hooks/useAccount';
import { useAccountStatus } from 'modules/accountStatus/hooks/useAccountStatus';
import { useJWTManagerPermissions } from 'domains/jwtToken/hooks/useJWTManagerPermissions';

const NewProjectPageV2 = loadable(
  async () => {
    const module = await import('./NewProjectPageV2');

    return module.NewProjectPageV2;
  },
  {
    fallback: <OverlaySpinner />,
  },
);

const chainsPath = chainsRoutesConfig.chains.generatePath();

function NewProjectPageV2Loader() {
  const { isAccountInitializing, permissionsInitialized } = useAccount();

  const { initialLoading: accountStatusLoading, isFreemium } =
    useAccountStatus();
  const {
    allowedJWTsCountLoading,
    hasReadAccess: hasProjectsAccess,
    isAllowedJWTsCountLoaded,
  } = useJWTManagerPermissions();

  const isLoading =
    isAccountInitializing ||
    accountStatusLoading ||
    allowedJWTsCountLoading ||
    !permissionsInitialized;

  if (isLoading) {
    return <OverlaySpinner />;
  }

  const shouldForceRedirect =
    !isFreemium && !hasProjectsAccess && isAllowedJWTsCountLoaded;

  return (
    <GuardAccountPermission
      permission={AccountPermission.UsageData}
      shouldForceRedirect={shouldForceRedirect}
      shouldHideAlert
      shouldRedirect
    >
      <ProjectsAccessGuard fallback={chainsRoutesConfig.chains.generatePath()}>
        <NewProjectPageV2 />
      </ProjectsAccessGuard>
    </GuardAccountPermission>
  );
}

export const NewProjectPageV2Container = () => {
  return (
    <Page>
      <NoSSRGuard>
        <LoggingInGuard fallback={<Navigate to={chainsPath} />}>
          <NewProjectPageV2Loader />
        </LoggingInGuard>
      </NoSSRGuard>
    </Page>
  );
};
